import {
  ImageScaleView,
  View,
  ViewOpts,
  animate,
} from '@play-co/timestep-core/ui';

/**
 * Interface representing the data associated with each snowflake
 * @interface SnowflakeData
 * @property {number} speed - The vertical falling speed of the snowflake
 * @property {number} wobbleSpeed - The speed of the horizontal wobble movement
 * @property {number} wobbleRange - The maximum horizontal distance the snowflake can wobble
 */
interface SnowflakeData {
  speed: number;
  wobbleSpeed: number;
  wobbleRange: number;
}

/**
 * A class that creates and manages an animated snow effect
 * @extends View
 *
 * @example
 * const snowEffect = new SnowEffect({
 *   superview: parentView,
 *   width: 800,
 *   height: 600
 * });
 *
 * // Start the snow effect
 * snowEffect.start();
 *
 * // Later, stop the effect
 * snowEffect.stop();
 */
export class SnowEffect extends View {
  /** Number of snowflakes to create */
  private static readonly NUM_SNOWFLAKES = 50;
  /** Minimum scale factor for snowflakes */
  private static readonly MIN_SCALE = 0.5;
  /** Range of random scale variation */
  private static readonly SCALE_RANGE = 0.5;
  /** Base speed for snowflake falling motion */
  private static readonly BASE_SPEED = 0.1;
  /** Base speed for snowflake wobble motion */
  private static readonly WOBBLE_BASE = 0.001;
  /** Maximum range of horizontal wobble motion */
  private static readonly WOBBLE_RANGE = 20;

  /** Array of snowflake view instances */
  private readonly snowflakes: ImageScaleView[] = [];
  /** Array of data objects containing movement parameters for each snowflake */
  private readonly snowflakeData: SnowflakeData[] = [];

  /** ID of the current animation frame request */
  private animationFrameId: number | null = null;
  /** Timestamp of the last animation update */
  private lastTimestamp = 0;
  /** Flag indicating whether the snow effect is currently active */
  private isActive = false;

  /**
   * Creates a new SnowEffect instance
   * @param {ViewOpts} opts - Configuration options for the view
   *
   * @example
   * const snowEffect = new SnowEffect({
   *   superview: parentView,
   *   width: 800,
   *   height: 600
   * });
   */
  constructor(opts: ViewOpts) {
    super({ ...opts, visible: false });
    this.initializeSnowflakes();
  }

  /**
   * Initializes the snowflakes for the snow effect
   *
   * Creates a set of snowflake instances based on NUM_SNOWFLAKES constant.
   * Each snowflake is given random properties within defined ranges for:
   * - Scale (affects size)
   * - Position (within view bounds)
   * - Movement parameters (speed, wobble)
   *
   * @private
   */
  private initializeSnowflakes(): void {
    const { width, height } = this.style;

    for (let i = 0; i < SnowEffect.NUM_SNOWFLAKES; i++) {
      const scale =
        Math.random() * SnowEffect.SCALE_RANGE + SnowEffect.MIN_SCALE;

      this.snowflakes.push(
        new ImageScaleView({
          superview: this,
          image: 'assets/ui/match2/common/snowflake.png',
          width: 12,
          height: 12,
          scale,
          x: Math.random() * width,
          y: Math.random() * height,
          centerOnOrigin: true,
        }),
      );

      this.snowflakeData.push({
        speed: scale * SnowEffect.BASE_SPEED,
        wobbleSpeed: SnowEffect.WOBBLE_BASE * scale,
        wobbleRange: SnowEffect.WOBBLE_RANGE,
      });
    }
  }

  /**
   * Starts the snow effect animation
   *
   * Makes the effect visible with a fade-in animation and initiates
   * the animation loop using requestAnimationFrame.
   * Does nothing if the effect is already active.
   *
   * @public
   *
   * @example
   * snowEffect.start();
   */
  public start(): void {
    if (this.isActive) return;

    this.isActive = true;
    this.updateOpts({ visible: true, opacity: 0 });
    animate(this).now({ opacity: 1 }, 500);

    this.lastTimestamp = performance.now();
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
  }

  /**
   * Stops the snow effect animation
   *
   * Fades out the effect and stops the animation loop.
   * Sets the view to invisible after fade-out completes.
   * Does nothing if the effect is already inactive.
   *
   * @public
   *
   * @example
   * snowEffect.stop();
   */
  public stop(): void {
    if (!this.isActive) return;

    this.isActive = false;
    animate(this)
      .now({ opacity: 0 }, 500)
      .then(() => this.updateOpts({ visible: false }));

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Handles the animation frame updates
   *
   * Called by requestAnimationFrame to update snowflake positions
   * and schedule the next frame if the effect is still active.
   *
   * @param {number} timestamp - The current timestamp from requestAnimationFrame
   * @private
   */
  private animate(timestamp: number): void {
    if (!this.isActive) return;

    this.updateSnow(timestamp);
    this.animationFrameId = requestAnimationFrame(this.animate.bind(this));
  }

  /**
   * Updates the position of all snowflakes
   *
   * Calculates new positions based on:
   * - Time elapsed since last update (for consistent speed)
   * - Individual snowflake parameters (speed, wobble)
   * - View boundaries (for wrapping)
   *
   * @param {number} timestamp - The current animation timestamp
   * @private
   */
  private updateSnow(timestamp: number): void {
    const dt = this.lastTimestamp ? timestamp - this.lastTimestamp : 0;
    this.lastTimestamp = timestamp;

    const { width, height } = this.style;

    for (let i = 0; i < this.snowflakes.length; i++) {
      const snowflake = this.snowflakes[i];
      const data = this.snowflakeData[i];

      // Update horizontal position (wobble)
      snowflake.style.offsetX =
        Math.sin(timestamp * data.wobbleSpeed) * data.wobbleRange;

      // Update vertical position
      snowflake.style.y += dt * data.speed;

      // Reset position if snowflake is below screen
      if (snowflake.style.y > height + snowflake.style.height) {
        snowflake.style.x = Math.random() * width;
        snowflake.style.y = -snowflake.style.height;
      }
    }
  }
}
